<template>
  <SelectCertifier
    v-if="certifiers?.length"
    :id="certifierModalId"
    :certifiers="certifiers"
    :key="certifierModalId"
    @onSelect="printWithCertifier"/>
</template>

<script>
import Alert from "../../../helpers/alert";
import Base64 from "../../../helpers/base64";
import Config from "../../../config";
import OS from "../../../helpers/os";
import Loader from "../../../helpers/loader";
import http from "../../../helpers/http";
import SelectCertifier from "../../../components/SelectCertificator";
import {confirmarAssinaturaSemSelo} from "./signatureSealWarning";
import {
  canSignMedicalRecord,
  medicalRecordPrintUrl,
  printMedicalRecordPdf,
  uploadSignedMedicalRecordPdf,
} from "./printMedicalRecord";

export default {
  name: "MedicalRecordPrinter",
  components: {SelectCertifier},
  emits: ["printed"],
  props: {
    id: {
      type: String,
      required: true,
    },
    schedule: Object,
    certifiers: Array,
  },
  data() {
    return {
      selectedRecord: null,
    };
  },
  computed: {
    certifierModalId() {
      return `modal_certifier_medical_record_${this.id}`;
    },
    userAuth() {
      return this.$store.state.auth.user;
    },
    place() {
      return this.$store.state.auth.place;
    },
    professionalId() {
      return this.schedule?.id_prof;
    },
  },
  methods: {
    canSign(record) {
      return canSignMedicalRecord(record, this.schedule);
    },
    patientId(record) {
      return record?.id_patient || this.schedule?.id_patient;
    },
    async start(record) {
      this.selectedRecord = record;
      const latest = await this.refreshRecord(record);

      if (!this.canSign(latest) || latest.medrec_signed) {
        return this.printWithCertifier(null);
      }

      const modalEl = document.getElementById(this.certifierModalId);
      if (!modalEl) {
        return this.printWithCertifier({id: "none"});
      }

      bootstrap.Modal.getOrCreateInstance(modalEl).toggle();
    },
    async refreshRecord(record) {
      const professionalId = this.professionalId;
      const patientId = this.patientId(record);
      if (!professionalId || !patientId || !record?.id_medrec) {
        return record;
      }

      try {
        const response = await http.get(
          medicalRecordPrintUrl({
            professionalId,
            patientId,
            medrecId: record.id_medrec,
          }),
        );
        const latest = response.data;
        Object.assign(record, {
          medrec_generated: latest.medrec_generated,
          medrec_signed: latest.medrec_signed,
          id_authen: latest.id_authen,
          shared_readonly: latest.shared_readonly,
          id_prof: latest.id_prof,
        });
        this.selectedRecord = record;
        return record;
      } catch {
        return record;
      }
    },
    async printWithCertifier(certifier) {
      const record = this.selectedRecord;
      if (!record) {
        return;
      }

      const seguir = await confirmarAssinaturaSemSelo({
        generated: record.medrec_generated,
        signed: record.medrec_signed,
        certifier,
      });

      if (!seguir) {
        return;
      }

      const certifierId = this.canSign(record) ? (certifier?.id ?? "none") : null;

      try {
        const payload = await printMedicalRecordPdf({
          professionalId: this.professionalId,
          patientId: this.patientId(record),
          medrecId: record.id_medrec,
          certifierId,
        });

        this.syncRecord(record, payload.medicalRecord);

        if (payload.medicalRecord?.medrec_generated && (
          payload.signed || !certifierId || certifierId === "none"
        )) {
          return this.emitPrinted(payload);
        }

        if (certifierId === "vidaas") {
          return this.signCloud(record, certifier, payload);
        }

        if (certifierId === "token") {
          return this.signToken(payload, record);
        }

        this.emitPrinted(payload);
      } catch {
        // printMedicalRecordPdf já exibe o erro
      }
    },
    syncRecord(record, latest) {
      if (!latest) {
        return;
      }

      Object.assign(record, {
        medrec_generated: latest.medrec_generated,
        medrec_signed: latest.medrec_signed,
        id_authen: latest.id_authen,
      });
    },
    emitPrinted(payload) {
      this.$emit("printed", {
        pdfSource: payload.pdfSource,
        pdfFileName: payload.pdfFileName,
        signed: payload.signed,
        medicalRecord: payload.medicalRecord,
      });
    },
    async signCloud(record, certifier, payload) {
      Loader.show("Assinando digitalmente", "Autorize a transação no aplicativo, se solicitado.");

      try {
        const response = await http.post(`${certifier.url}/medical-record/${record.id_medrec}`);
        this.syncRecord(record, {
          medrec_generated: true,
          medrec_signed: true,
          id_authen: record.id_authen,
        });
        Loader.close();
        this.emitPrinted({
          pdfSource: `data:application/pdf;base64,${response.data.base64_signed}`,
          pdfFileName: payload.pdfFileName,
          signed: true,
          medicalRecord: record,
        });
      } catch (error) {
        Loader.close();
        Alert.toastError(error.response?.data?.message || "Erro ao assinar o prontuário.");
      }
    },
    async signToken(fileToBeSign, record) {
      if (!window.WebSocket) {
        Alert.toastError("Seu navegador não tem suporte a conexão websocket.");
        return;
      }

      let certificatePassword = sessionStorage.getItem("certificate_password");
      if (!certificatePassword) {
        const {value: password} = await Swal.fire({
          title: "Senha do certificado",
          input: "text",
          inputLabel: "Informe a senha",
          confirmButtonText: "Assinar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#780f5a",
          showCancelButton: true,
          inputValidator: (value) => {
            if (!value) {
              return "Informe a senha do certificado digital!";
            }
          },
        });

        if (password) {
          certificatePassword = Base64.encode(password);
        }

        if (!certificatePassword) {
          return;
        }
      }

      Loader.show("Assinando digitalmente", "Aguarde o assinador Ubbimed.");

      let certificate = null;

      if (OS.getOS() === "Windows") {
        const wsCertificates = new WebSocket(Config.protocol_ws + "://localhost:8044/certificates");
        wsCertificates.onopen = () => {
          wsCertificates.send("1");
        };

        wsCertificates.onmessage = async (event) => {
          const certificatesRaw = JSON.parse(event.data);
          wsCertificates.close(1000);

          if (certificatesRaw.length <= 0) {
            Loader.close();
            Alert.toastError("Nenhum certificado encontrado.");
            return;
          }

          const certificates = certificatesRaw.sort((certificateOne, certificateTwo) => {
            const expiryDateOne = new Date(certificateOne.expiryDate.toUpperCase().replace("BRT", "GMT-0300"));
            const expiryDateTwo = new Date(certificateTwo.expiryDate.toUpperCase().replace("BRT", "GMT-0300"));
            return expiryDateTwo - expiryDateOne;
          });

          if (certificates.length === 1) {
            certificate = certificates[0].alias;
            return;
          }

          const alias = certificates.map((item) => item.alias);
          const {value: aliasSelected} = await Swal.fire({
            title: "Certificados instalados",
            input: "select",
            inputOptions: {alias},
            inputPlaceholder: "Selecione um certificado",
          });
          certificate = aliasSelected;
        };

        wsCertificates.onerror = () => {
          Loader.close();
          Swal.fire({
            title: "Opsss",
            text: "Não conseguimos identificar o Assinador Ubbimed instalado.",
            icon: "warning",
            confirmButtonText: "OK",
            confirmButtonColor: "#780f5a",
          }).then(() => {
            window.open("/assinador/download");
          });
        };
      }

      let connectedPdfSign = false;
      const wsSignConnection = new WebSocket("ws://localhost:8044/pdf/sign");
      wsSignConnection.onopen = () => {
        connectedPdfSign = true;
        wsSignConnection.send(JSON.stringify({
          certificate_pfx_as_base64: null,
          certificate_password: Base64.decode(certificatePassword),
          reason: "Assinatura de prontuário Ubbimed.",
          location: this.place?.place_name,
          contact: `${this.userAuth?.user_name} <${this.userAuth?.user_email}>`,
          certificate_alias: certificate,
          file_id: record.id_medrec,
          file_as_base64: fileToBeSign.fileBase64,
        }));
      };

      wsSignConnection.onmessage = (message) => {
        const responseSignature = JSON.parse(message.data);
        const file = Base64.str2Blob(responseSignature.file_signed_as_base64);
        const formData = new FormData();
        formData.append("file", file, "file.pdf");

        uploadSignedMedicalRecordPdf({
          professionalId: this.professionalId,
          patientId: this.patientId(record),
          medrecId: record.id_medrec,
          formData,
        }).then((response) => {
          Alert.toastSuccess("Assinatura realizada com sucesso.");
          this.syncRecord(record, response.data.medical_record);
          Loader.close();
          this.emitPrinted({
            pdfSource: `data:application/pdf;base64,${response.data.file_base64}`,
            pdfFileName: response.data.file_name || fileToBeSign.pdfFileName,
            signed: true,
            medicalRecord: response.data.medical_record,
          });

          if (!sessionStorage.getItem("certificate_password")) {
            Swal.fire({
              text: "Quer salvar a senha para as próximas assinaturas?",
              icon: "success",
              showCloseButton: true,
              showCancelButton: true,
              confirmButtonText: "Sim, salvar senha",
            }).then((result) => {
              if (result.isConfirmed) {
                sessionStorage.setItem("certificate_password", certificatePassword);
              }
            });
          }
        }).catch((error) => {
          Loader.close();
          Alert.toastError(error.response?.data?.message || "Erro ao gravar o prontuário assinado.");
        });

        wsSignConnection.close(1000);
      };

      wsSignConnection.onerror = () => {
        Loader.close();
        if (connectedPdfSign) {
          Alert.toastError("Ocorreu um erro inesperado com o assinador Ubbimed.");
        } else {
          Alert.toastError("Não foi possível conectar ao assinador Ubbimed. Verifique se ele está instalado e em execução.");
        }
      };
    },
  },
};
</script>
